/**
 * OAuthConsentPage — /oauth/consent?request_id=<id>  (PRI-462)
 *
 * Human side of the OAuth 2.1 authorization flow for the hosted MCP endpoint.
 * The API's GET /oauth/authorize redirects here with a pending request_id.
 * The page fetches the request details (client name, scope, the user's API
 * keys) using the signed-in user's Clerk JWT, lets the user pick which API
 * key the OAuth client should act through, and Approve/Deny → the API
 * responds with a redirect_to URL back to the OAuth client's callback.
 *
 * Uses usePrimateAuth() (not useAuth()) so it works in both AppRoutesWithAuth
 * and AppRoutesNoAuth — including when Clerk is not configured (test env).
 */

import { useState, useEffect, useCallback } from 'react'
import { useSearchParams, useLocation, Navigate } from 'react-router'
import { usePrimateAuth } from '../primate/auth/usePrimateAuth'
import {
  getConsentRequest,
  approveConsentRequest,
  denyConsentRequest,
  type ConsentRequest,
} from '../primate/api/oauth'
import { trackOAuthConsentDecision } from '../lib/analytics'

// ── Brand palette — forced light mode (brand-guidelines-v3.2) ─────────────────
const L = {
  bgPage:       '#F2F0EB',
  bgSurface:    '#FAFAF7',
  border:       '#D4D0C8',
  textPrimary:  '#1A1916',
  textMuted:    '#7A7669',
  signal:       '#1B4FE0',
  signalBg:     '#EEF2FF',
  danger:       '#DC2626',
  dangerBg:     '#FEF2F2',
  dangerBorder: '#FECACA',
}

type PageState = 'loading' | 'ready' | 'submitting' | 'error'
type ErrorKind = 'not_found' | 'expired' | 'no_keys' | 'missing_id' | 'network'

interface PageError {
  kind: ErrorKind
  message: string
}

// ── LogoMark ──────────────────────────────────────────────────────────────────
function LogoMark() {
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <img
        src="/Darwin_v3_vinyl_transparent.png"
        alt=""
        style={{ height: 32, width: 'auto', objectFit: 'contain', marginRight: 10 }}
      />
      <div style={{ lineHeight: 1.15 }}>
        <span style={{ fontSize: 18, fontWeight: 700, color: L.textPrimary, letterSpacing: '-0.03em' }}>
          Primate
        </span>
        <span style={{ fontSize: 18, fontWeight: 700, color: L.signal, letterSpacing: '-0.03em' }}>
          Intelligence
        </span>
      </div>
    </div>
  )
}

function classifyError(err: unknown): PageError {
  const e = err as { code?: string; status?: number; message?: string }
  if (e.status === 404) {
    return { kind: 'not_found', message: 'This authorization request was not found.' }
  }
  if (e.status === 410) {
    return { kind: 'expired', message: 'This request has expired. Please start again.' }
  }
  return {
    kind: 'network',
    message: e.message ?? 'Something went wrong. Please try again.',
  }
}

export default function OAuthConsentPage() {
  // ── All hooks unconditionally at the top (no early returns before hooks) ──
  const [searchParams] = useSearchParams()
  const location = useLocation()
  // usePrimateAuth is context-safe: works inside ClerkProvider and NoAuthProvider
  const { isSignedIn, authLoaded } = usePrimateAuth()
  const requestId = searchParams.get('request_id') ?? ''

  const [state, setState] = useState<PageState>('loading')
  const [error, setError] = useState<PageError | null>(null)
  const [consent, setConsent] = useState<ConsentRequest | null>(null)
  const [selectedKeyId, setSelectedKeyId] = useState('')
  const [authFailed, setAuthFailed] = useState(false)

  const returnUrl = encodeURIComponent(location.pathname + location.search)
  const shouldRedirectToLogin = authLoaded && (!isSignedIn || authFailed)

  const loadRequest = useCallback(async () => {
    setState('loading')
    setError(null)
    try {
      const data = await getConsentRequest(requestId)
      if (!data.api_keys || data.api_keys.length === 0) {
        setConsent(data)
        setError({
          kind: 'no_keys',
          message: 'You need an API key before you can authorize this app.',
        })
        setState('error')
        return
      }
      setConsent(data)
      // Preselect when there's exactly one key
      setSelectedKeyId(data.api_keys.length === 1 ? data.api_keys[0].id : '')
      setState('ready')
    } catch (err) {
      const e = err as { status?: number }
      if (e.status === 401) {
        // Clerk JWT missing/invalid — send to sign-in with a return URL
        setAuthFailed(true)
        return
      }
      setError(classifyError(err))
      setState('error')
    }
  }, [requestId])

  useEffect(() => {
    if (!authLoaded || shouldRedirectToLogin) return
    if (!requestId) {
      setError({ kind: 'missing_id', message: 'No authorization request found in this URL.' })
      setState('error')
      return
    }
    void loadRequest()
  }, [authLoaded, shouldRedirectToLogin, requestId, loadRequest])

  // ── Auth / loading guards (after all hooks) ───────────────────────────────
  if (!authLoaded) {
    return (
      <div style={{ minHeight: '100vh', background: L.bgPage, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ color: L.textMuted, fontSize: 14 }}>Loading…</div>
      </div>
    )
  }

  if (shouldRedirectToLogin) {
    return <Navigate to={`/login?redirect_url=${returnUrl}`} replace />
  }

  // ── Handlers ──────────────────────────────────────────────────────────────
  async function handleDecision(decision: 'approve' | 'deny') {
    if (!requestId) return
    setState('submitting')
    try {
      const res =
        decision === 'approve'
          ? await approveConsentRequest(requestId, selectedKeyId)
          : await denyConsentRequest(requestId)
      trackOAuthConsentDecision({ decision })
      window.location.assign(res.redirect_to)
    } catch (err) {
      const e = err as { status?: number }
      if (e.status === 401) {
        setAuthFailed(true)
        return
      }
      setError(classifyError(err))
      setState('error')
    }
  }

  // ── Styles ────────────────────────────────────────────────────────────────
  const card: React.CSSProperties = {
    background: L.bgSurface,
    border: `1px solid ${L.border}`,
    borderRadius: 8,
    padding: '40px 36px',
    maxWidth: 480,
    width: '100%',
    boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
  }

  const btn = (variant: 'primary' | 'danger' | 'neutral', disabled?: boolean): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 20px',
    borderRadius: 6,
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    transition: 'background 150ms',
    ...(variant === 'primary' ? {
      background: L.signal,
      color: '#fff',
      border: 'none',
    } : variant === 'danger' ? {
      background: 'transparent',
      color: L.danger,
      border: `1px solid ${L.dangerBorder}`,
    } : {
      background: 'transparent',
      color: L.textPrimary,
      border: `1px solid ${L.border}`,
    }),
  })

  const label: React.CSSProperties = {
    fontFamily: 'Inter, sans-serif',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    color: L.textMuted,
    marginBottom: 6,
  }

  const isRecoverable = error?.kind === 'network'

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: '100vh',
        background: L.bgPage,
        fontFamily: 'Inter, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '48px 16px 80px',
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: 40 }}>
        <LogoMark />
      </div>

      {/* ── Loading ── */}
      {state === 'loading' && (
        <div style={card}>
          <div style={{ color: L.textMuted, fontSize: 14 }}>Loading authorization request…</div>
        </div>
      )}

      {/* ── Error ── */}
      {state === 'error' && error && (
        <div style={card} data-testid="consent-error">
          <div
            style={{
              background: error.kind === 'no_keys' ? L.signalBg : L.dangerBg,
              border: `1px solid ${error.kind === 'no_keys' ? '#C7D7FF' : L.dangerBorder}`,
              borderRadius: 6,
              padding: '16px 20px',
              marginBottom: 20,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 15, color: error.kind === 'no_keys' ? L.textPrimary : L.danger, marginBottom: 4 }}>
              {error.kind === 'no_keys' ? 'No API keys yet' : 'Unable to continue'}
            </div>
            <div style={{ fontSize: 14, color: L.textMuted, lineHeight: 1.6 }}>
              {error.message}
            </div>
          </div>
          {error.kind === 'no_keys' && (
            <div style={{ fontSize: 13, color: L.textMuted, lineHeight: 1.6 }}>
              Create an API key in{' '}
              <a href="/dashboard/api-keys" style={{ color: L.signal, textDecoration: 'none', fontWeight: 600 }}>
                Dashboard → API Keys
              </a>
              , then restart the authorization from your app.
            </div>
          )}
          {isRecoverable && (
            <button data-testid="retry-btn" style={btn('neutral')} onClick={() => void loadRequest()}>
              Try again
            </button>
          )}
        </div>
      )}

      {/* ── Ready / Submitting ── */}
      {(state === 'ready' || state === 'submitting') && consent && (
        <div style={card}>
          {/* Intent description */}
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontWeight: 700, fontSize: 20, color: L.textPrimary, marginBottom: 10, lineHeight: 1.3 }} data-testid="consent-title">
              Authorize {consent.client_name}
            </div>
            <div style={{ fontSize: 14, color: L.textMuted, lineHeight: 1.65 }}>
              <strong style={{ color: L.textPrimary }}>{consent.client_name}</strong>{' '}
              wants access to your Primate Vision account (scope: {consent.scope}).
            </div>
          </div>

          {/* API key picker */}
          <div style={{ marginBottom: 28 }}>
            <div style={label}>Act through this API key</div>
            <div role="radiogroup" aria-label="API key" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {consent.api_keys.map((key) => {
                const selected = selectedKeyId === key.id
                return (
                  <label
                    key={key.id}
                    data-testid={`key-option-${key.id}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 14px',
                      borderRadius: 6,
                      border: `1px solid ${selected ? L.signal : L.border}`,
                      background: selected ? L.signalBg : 'transparent',
                      cursor: 'pointer',
                      fontSize: 14,
                      color: L.textPrimary,
                    }}
                  >
                    <input
                      type="radio"
                      name="api_key"
                      value={key.id}
                      checked={selected}
                      onChange={() => setSelectedKeyId(key.id)}
                      style={{ accentColor: L.signal }}
                    />
                    <span style={{ fontWeight: 600 }}>{key.label}</span>
                    <span style={{ fontFamily: 'ui-monospace, "Cascadia Code", monospace', fontSize: 13, color: L.textMuted }}>
                      ••••{key.last4}
                    </span>
                  </label>
                )
              })}
            </div>
          </div>

          {/* Security note */}
          <div
            style={{
              background: L.signalBg,
              border: `1px solid #C7D7FF`,
              borderRadius: 6,
              padding: '12px 16px',
              marginBottom: 28,
              fontSize: 13,
              color: L.textMuted,
              lineHeight: 1.6,
            }}
          >
            Only approve if <em>you</em> initiated this connection. The app will be able to
            make live API calls on your account through the selected key.
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              data-testid="allow-btn"
              style={btn('primary', state === 'submitting' || !selectedKeyId)}
              disabled={state === 'submitting' || !selectedKeyId}
              onClick={() => void handleDecision('approve')}
            >
              {state === 'submitting' ? 'Working…' : 'Allow'}
            </button>
            <button
              data-testid="deny-btn"
              style={btn('danger', state === 'submitting')}
              disabled={state === 'submitting'}
              onClick={() => void handleDecision('deny')}
            >
              Deny
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
